import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { listProjects } from '../../api/projects';
import type { GrandProjet } from '../../types/estimation';
import { computeN1, FALLBACK_RATIOS, FALLBACK_BENCHMARKS } from '../../utils/estimation';
import { InfoTip } from '../shared/InfoTip';
import { fmt, fmtCHF } from '../../utils/format';
import { toast } from '../../stores/uiStore';
import styles from './PortfolioOptimizerPage.module.css';

interface ProjectMetrics {
  project: GrandProjet;
  cost: number;
  logements: number;
  emplois: number;
  inhabitants: number;
  sbp: number;
  score: number;
  included: boolean;
}

export function PortfolioOptimizerPage() {
  const [projects, setProjects] = useState<GrandProjet[]>([]);
  const [loading, setLoading] = useState(true);
  const [budget, setBudget] = useState(500_000_000); // 500M CHF default
  const [wHousing, setWHousing] = useState(40);
  const [wEmployment, setWEmployment] = useState(30);
  const [wEfficiency, setWEfficiency] = useState(30);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const { t } = useTranslation(['optimizer', 'common']);

  useEffect(() => {
    listProjects({ is_custom: true }).then((data) => {
      setProjects(data);
      setLoading(false);
    }).catch(() => {
      toast.error(t('common:failedToLoad'));
      setLoading(false);
    });
  }, []);

  // Compute metrics for each project
  const projectMetrics: ProjectMetrics[] = useMemo(() => {
    if (projects.length === 0) return [];

    const items = projects.map((p) => {
      const ha = Number(p.ha_total ?? 0);
      const haZi = Number(p.ha_industriel ?? 0);
      const result = computeN1(Math.max(ha, 1), haZi, FALLBACK_RATIOS, FALLBACK_BENCHMARKS);
      return {
        project: p,
        cost: result.cost_total,
        logements: p.logements || result.logements,
        emplois: p.emplois || result.emplois,
        inhabitants: result.inhabitants,
        sbp: p.sbp_total || result.sbp_total,
        score: 0,
        included: false,
      };
    });

    // Normalize weights
    const totalW = wHousing + wEmployment + wEfficiency;
    const nH = totalW > 0 ? wHousing / totalW : 0.33;
    const nE = totalW > 0 ? wEmployment / totalW : 0.33;
    const nF = totalW > 0 ? wEfficiency / totalW : 0.33;

    // Compute marginal value scores (value per CHF of cost)
    const maxLogPerCHF = Math.max(...items.map((i) => i.cost > 0 ? i.logements / i.cost : 0), 0.001);
    const maxEmpPerCHF = Math.max(...items.map((i) => i.cost > 0 ? i.emplois / i.cost : 0), 0.001);
    const maxSBPPerCHF = Math.max(...items.map((i) => i.cost > 0 ? i.sbp / i.cost : 0), 0.001);

    for (const item of items) {
      if (item.cost <= 0) { item.score = 0; continue; }
      const logScore = (item.logements / item.cost) / maxLogPerCHF;
      const empScore = (item.emplois / item.cost) / maxEmpPerCHF;
      const effScore = (item.sbp / item.cost) / maxSBPPerCHF;
      item.score = nH * logScore + nE * empScore + nF * effScore;
    }

    // Sort by score descending (greedy knapsack)
    items.sort((a, b) => b.score - a.score);

    // Greedy selection within budget
    let remaining = budget;
    for (const item of items) {
      const manualOverride = overrides[item.project.id];
      if (manualOverride === false) {
        item.included = false;
        continue;
      }
      if (manualOverride === true) {
        item.included = true;
        remaining -= item.cost;
        continue;
      }
      if (item.cost <= remaining && item.cost > 0) {
        item.included = true;
        remaining -= item.cost;
      }
    }

    return items;
  }, [projects, budget, wHousing, wEmployment, wEfficiency, overrides]);

  // Portfolio totals
  const totals = useMemo(() => {
    const included = projectMetrics.filter((m) => m.included);
    return {
      count: included.length,
      cost: included.reduce((s, m) => s + m.cost, 0),
      logements: included.reduce((s, m) => s + m.logements, 0),
      emplois: included.reduce((s, m) => s + m.emplois, 0),
      inhabitants: included.reduce((s, m) => s + m.inhabitants, 0),
      sbp: included.reduce((s, m) => s + m.sbp, 0),
      budgetUsed: budget > 0 ? (included.reduce((s, m) => s + m.cost, 0) / budget) * 100 : 0,
    };
  }, [projectMetrics, budget]);

  function toggleProject(projectId: string) {
    setOverrides((prev) => {
      const current = prev[projectId];
      if (current === undefined) return { ...prev, [projectId]: false }; // force exclude
      if (current === false) return { ...prev, [projectId]: true }; // force include
      const next = { ...prev };
      delete next[projectId]; // back to auto
      return next;
    });
  }

  // Pareto chart data
  const paretoData = useMemo(() => {
    const maxCost = Math.max(...projectMetrics.map((m) => m.cost), 1);
    const maxLog = Math.max(...projectMetrics.map((m) => m.logements), 1);
    return projectMetrics.map((m) => ({
      ...m,
      x: (m.cost / maxCost) * 100,
      y: 100 - (m.logements / maxLog) * 100,
    }));
  }, [projectMetrics]);

  if (loading) return <div className={styles.loading}>{t('common:loading')}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>{t('optimizer:title')}</h2>
          <div className={styles.headerSub}>
            {t('optimizer:subtitle')}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.controlCard}>
          <div className={styles.controlTitle}>{t('optimizer:budgetConstraint')}<InfoTip text={t('optimizer:tooltips.budgetConstraint')} /></div>
          <div className={styles.sliderRow}>
            <span className={styles.sliderLabel}>{t('optimizer:totalBudget')}</span>
            <input
              type="range"
              className={styles.slider}
              min={50_000_000}
              max={2_000_000_000}
              step={10_000_000}
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
            />
            <span className={styles.sliderValue}>{fmtCHF(budget)}</span>
          </div>
          <input
            type="number"
            className={styles.budgetInput}
            value={budget}
            onChange={(e) => setBudget(Number(e.target.value) || 0)}
            placeholder={t('optimizer:budgetPlaceholder')}
          />
        </div>
        <div className={styles.controlCard}>
          <div className={styles.controlTitle}>{t('optimizer:objectiveWeights')}<InfoTip text={t('optimizer:tooltips.objectiveWeights')} /></div>
          <div className={styles.sliderRow}>
            <span className={styles.sliderLabel}>{t('optimizer:housing')}</span>
            <input
              type="range" className={styles.slider} min={0} max={100} step={5}
              value={wHousing} onChange={(e) => setWHousing(Number(e.target.value))}
            />
            <span className={styles.sliderValue}>{wHousing}%</span>
          </div>
          <div className={styles.sliderRow}>
            <span className={styles.sliderLabel}>{t('optimizer:employment')}</span>
            <input
              type="range" className={styles.slider} min={0} max={100} step={5}
              value={wEmployment} onChange={(e) => setWEmployment(Number(e.target.value))}
            />
            <span className={styles.sliderValue}>{wEmployment}%</span>
          </div>
          <div className={styles.sliderRow}>
            <span className={styles.sliderLabel}>{t('optimizer:efficiencySBP')}</span>
            <input
              type="range" className={styles.slider} min={0} max={100} step={5}
              value={wEfficiency} onChange={(e) => setWEfficiency(Number(e.target.value))}
            />
            <span className={styles.sliderValue}>{wEfficiency}%</span>
          </div>
        </div>
      </div>

      {/* Results summary */}
      <div className={styles.summaryRow}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{totals.count}</div>
          <div className={styles.summaryLabel}>{t('optimizer:selectedProjects')}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{fmtCHF(totals.cost)}</div>
          <div className={styles.summaryLabel}>{t('optimizer:totalCost')}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{fmt(totals.logements)}</div>
          <div className={styles.summaryLabel}>{t('optimizer:logements')}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{fmt(totals.emplois)}</div>
          <div className={styles.summaryLabel}>{t('optimizer:emplois')}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{fmt(totals.inhabitants)}</div>
          <div className={styles.summaryLabel}>{t('optimizer:inhabitants')}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{totals.budgetUsed.toFixed(0)}%</div>
          <div className={styles.summaryLabel}>{t('optimizer:budgetUsed')}</div>
        </div>
      </div>

      {/* Pareto scatter */}
      <div className={styles.paretoSection}>
        <div className={styles.sectionTitle}>{t('optimizer:costVsOutput')}<InfoTip text={t('optimizer:tooltips.paretoChart')} /></div>
        <div className={styles.paretoChart}>
          <div className={styles.paretoAxisLabel} style={{ bottom: 4, left: '50%', transform: 'translateX(-50%)' }}>
            {t('optimizer:costRelative')}
          </div>
          <div className={styles.paretoAxisLabel} style={{ top: '50%', left: 4, transform: 'rotate(-90deg) translateX(-50%)', transformOrigin: 'left' }}>
            {t('optimizer:logements')}
          </div>
          {paretoData.map((d) => (
            <div
              key={d.project.id}
              className={styles.paretoPoint}
              style={{
                left: `${Math.max(5, Math.min(95, d.x))}%`,
                top: `${Math.max(5, Math.min(95, d.y))}%`,
                background: d.included ? '#6366f1' : 'rgba(255,255,255,0.15)',
                border: overrides[d.project.id] !== undefined ? '2px solid #f39c12' : '2px solid transparent',
              }}
              title={`${d.project.name}: ${fmt(d.logements)} log, ${fmtCHF(d.cost)}`}
              onClick={() => toggleProject(d.project.id)}
            />
          ))}
        </div>
      </div>

      {/* Project ranking table */}
      <div className={styles.tableSection}>
        <div className={styles.sectionTitle}>{t('optimizer:projectRanking')}</div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('optimizer:statusCol')}</th>
              <th>{t('optimizer:projectCol')}</th>
              <th>{t('optimizer:scoreCol')}</th>
              <th>{t('optimizer:costCol')}</th>
              <th>{t('optimizer:logementsCol')}</th>
              <th>{t('optimizer:emploisCol')}</th>
              <th>{t('optimizer:sbpCol')}</th>
              <th>{t('optimizer:actionCol')}</th>
            </tr>
          </thead>
          <tbody>
            {projectMetrics.map((m) => (
              <tr key={m.project.id} className={m.included ? styles.included : styles.excluded}>
                <td>{m.included ? '✓' : '—'}</td>
                <td>{m.project.name}</td>
                <td>
                  <div className={styles.scoreBar}>
                    <div className={styles.scoreBarInner}>
                      <div className={styles.scoreBarFill} style={{ width: `${m.score * 100}%` }} />
                    </div>
                    <span className={styles.scoreValue}>{(m.score * 100).toFixed(0)}</span>
                  </div>
                </td>
                <td>{fmtCHF(m.cost)}</td>
                <td>{fmt(m.logements)}</td>
                <td>{fmt(m.emplois)}</td>
                <td>{fmt(m.sbp)} m²</td>
                <td>
                  <button
                    className={`${styles.toggleBtn} ${overrides[m.project.id] !== undefined ? styles.toggleBtnActive : ''}`}
                    onClick={() => toggleProject(m.project.id)}
                  >
                    {overrides[m.project.id] === true ? t('optimizer:forcedIn') :
                     overrides[m.project.id] === false ? t('optimizer:forcedOut') : t('optimizer:auto')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* What-if impact analysis */}
      {projectMetrics.length > 0 && (
        <div className={styles.paretoSection}>
          <div className={styles.sectionTitle}>{t('optimizer:whatIf')}</div>
          {(() => {
            const included = projectMetrics.filter((m) => m.included);
            if (included.length === 0) return <div className={styles.loading}>{t('optimizer:noProjectsSelected')}</div>;
            const highest = included.reduce((a, b) => a.cost > b.cost ? a : b);
            const withoutLog = totals.logements - highest.logements;
            const withoutEmp = totals.emplois - highest.emplois;
            return (
              <div className={styles.impactCard}>
                <div style={{ fontSize: 13, marginBottom: 12, color: 'var(--color-text-muted)' }}>
                  {t('optimizer:removing')} <strong style={{ color: 'var(--color-text)' }}>{highest.project.name}</strong> ({fmtCHF(highest.cost)})
                </div>
                <div className={styles.impactRow} style={{ marginBottom: 8 }}>
                  <div className={styles.impactLabel}>{t('optimizer:budgetFreed')}</div>
                  <div className={`${styles.impactDelta} ${styles.deltaPositive}`}>
                    +{fmtCHF(highest.cost)}
                  </div>
                </div>
                <div className={styles.impactRow} style={{ marginBottom: 8 }}>
                  <div className={styles.impactLabel}>{t('optimizer:logementsLost')}</div>
                  <div className={`${styles.impactDelta} ${styles.deltaNegative}`}>
                    -{fmt(highest.logements)} ({withoutLog} {t('optimizer:remaining')})
                  </div>
                </div>
                <div className={styles.impactRow}>
                  <div className={styles.impactLabel}>{t('optimizer:emploisLost')}</div>
                  <div className={`${styles.impactDelta} ${styles.deltaNegative}`}>
                    -{fmt(highest.emplois)} ({withoutEmp} {t('optimizer:remaining')})
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
